import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { adminGetTransactions } from "../../../redux/actions/secondaryMarketActions";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";

const TransactionDetailModal = ({ txn, onClose }) => {
  if (!txn) return null;
  const seller = txn.Seller || {};
  const buyer = txn.Buyer || {};
  const campaign = txn.Campaign || {};

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", maxWidth: "600px", width: "90%", maxHeight: "80vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ margin: 0 }}>Transaction #{txn.id}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer" }}>&times;</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "14px" }}>
          <div><strong>Status:</strong> {txn.status}</div>
          <div><strong>Campaign:</strong> {campaign.fund_name || campaign.corporation_legal_name || "—"}</div>
          <div><strong>Seller:</strong> {seller.firstname} {seller.lastname} ({seller.email})</div>
          <div><strong>Buyer:</strong> {buyer.firstname} {buyer.lastname} ({buyer.email})</div>
          <div><strong>Agreed Price:</strong> {txn.agreed_price != null ? `$${Number(txn.agreed_price).toLocaleString()}` : "—"}</div>
          <div><strong>Platform Fee:</strong> {txn.platform_fee != null ? `$${Number(txn.platform_fee).toLocaleString()}` : "—"}</div>
          <div><strong>Seller Proceeds:</strong> {txn.seller_proceeds != null ? `$${Number(txn.seller_proceeds).toLocaleString()}` : "—"}</div>
          <div><strong>Buyer Total:</strong> {txn.buyer_total != null ? `$${Number(txn.buyer_total).toLocaleString()}` : "—"}</div>
          <div><strong>Shares:</strong> {txn.shares_quantity}</div>
          <div><strong>Currency:</strong> {txn.currency}</div>
          <div><strong>Listing ID:</strong> {txn.listing_id}</div>
          <div><strong>Bid ID:</strong> {txn.bid_id}</div>
          <div><strong>Created:</strong> {new Date(txn.createdAt).toLocaleString()}</div>
          <div><strong>Settlement:</strong> {txn.settlement_date ? new Date(txn.settlement_date).toLocaleString() : "—"}</div>
        </div>
        {txn.notes && <div style={{ marginTop: "12px" }}><strong>Notes:</strong> {txn.notes}</div>}
      </div>
    </div>
  );
};

const SecondaryTransactionList = () => {
  const dispatch = useDispatch();
  const { transactions } = useSelector((state) => state.secondaryMarket);
  const [pageState, setPageState] = useState({ page: 0, pageSize: 20 });
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedTxn, setSelectedTxn] = useState(null);

  useEffect(() => {
    dispatch(adminGetTransactions(pageState.page + 1, pageState.pageSize, statusFilter));
  }, [dispatch, pageState.page, pageState.pageSize, statusFilter]);

  const formatCurrency = (amt) => {
    if (amt == null) return "—";
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 0 }).format(amt);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 1,
      minWidth: 70,
      renderCell: (params) => (
        <span
          style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => setSelectedTxn(params.row)}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "campaign",
      headerName: "Campaign",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => params.row.Campaign?.fund_name || params.row.Campaign?.corporation_legal_name || "—",
    },
    {
      field: "seller",
      headerName: "Seller",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const s = params.row.Seller;
        return s ? `${s.firstname || ""} ${s.lastname || ""}`.trim() : "—";
      },
    },
    {
      field: "buyer",
      headerName: "Buyer",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const b = params.row.Buyer;
        return b ? `${b.firstname || ""} ${b.lastname || ""}`.trim() : "—";
      },
    },
    { field: "agreed_price", headerName: "Agreed Price", width: 120, renderCell: (params) => formatCurrency(params.value) },
    { field: "platform_fee", headerName: "Platform Fee", width: 120, renderCell: (params) => formatCurrency(params.value) },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const colors = {
          COMPLETED: "#2e7d32", PENDING_PAYMENT: "#f57c00", PAID: "#1565c0",
          PENDING_TRANSFER: "#6a1b9a", FAILED: "#c62828", DISPUTED: "#c62828",
        };
        return (
          <span style={{
            padding: "3px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 600,
            color: colors[params.value] || "#616161",
            background: `${colors[params.value] || "#616161"}15`,
          }}>
            {params.value}
          </span>
        );
      },
    },
    {
      field: "settlement_date",
      headerName: "Settled",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString() : "—",
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
  ];

  return (
    <PageShell>
      <PageHead
        title="Secondary market · transactions"
        actions={
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: "4px", border: "1px solid #ccc" }}
          >
            <option value="">All Statuses</option>
            <option value="PENDING_PAYMENT">Pending Payment</option>
            <option value="PAID">Paid</option>
            <option value="PENDING_TRANSFER">Pending Transfer</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
            <option value="DISPUTED">Disputed</option>
          </select>
        }
      />
      <div className="list">
        <div className="listContainer">
          <DataGrid
            rows={transactions.items || []}
            columns={columns}
            loading={transactions.loading}
            rowCount={transactions.pagination?.total || 0}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={{ page: pageState.page, pageSize: pageState.pageSize }}
            paginationMode="server"
            onPaginationModelChange={(model) => setPageState(model)}
            autoHeight
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        </div>

        {selectedTxn && <TransactionDetailModal txn={selectedTxn} onClose={() => setSelectedTxn(null)} />}
      </div>
    </PageShell>
  );
};

export default SecondaryTransactionList;
